import React, { useEffect, useRef } from 'react';
import StableStreamingMessage from './StableStreamingMessage';

interface DebugStreamingMessageProps {
  content: string;
  isStreaming: boolean;
  messageIndex: number;
}

const DebugStreamingMessage: React.FC<DebugStreamingMessageProps> = ({ content, isStreaming, messageIndex }) => {
  const renderCountRef = useRef(0);
  const lastContentLengthRef = useRef(0);
  const contentLengthHistoryRef = useRef<number[]>([]);
  
  useEffect(() => {
    renderCountRef.current += 1;
    
    // Log significant events
    const currentLength = content.length;
    const lineCount = content.split('\n').length;
    
    // Log when content length changes significantly
    if (Math.abs(currentLength - lastContentLengthRef.current) > 100) {
      console.log(`[Message ${messageIndex}] Significant content change:`, {
        renderCount: renderCountRef.current,
        contentLength: currentLength,
        lineCount: lineCount,
        lengthDelta: currentLength - lastContentLengthRef.current,
        hasThinkingBlock: content.includes('<thinking>'),
        hasPythonCode: content.includes('```python'),
        isStreaming
      });
    }
    
    // Log every 10 renders
    if (renderCountRef.current % 10 === 0) {
      console.log(`[Message ${messageIndex}] Render milestone:`, {
        renderCount: renderCountRef.current,
        contentLength: currentLength,
        lineCount: lineCount,
        isStreaming
      });
    }
    
    // Track content length history
    contentLengthHistoryRef.current.push(currentLength);
    if (contentLengthHistoryRef.current.length > 100) {
      contentLengthHistoryRef.current.shift();
    }
    
    // Detect if content was truncated or replaced
    if (currentLength < lastContentLengthRef.current - 10) {
      console.warn(`[Message ${messageIndex}] Content DECREASED!`, {
        from: lastContentLengthRef.current,
        to: currentLength,
        delta: currentLength - lastContentLengthRef.current
      });
    }
    
    lastContentLengthRef.current = currentLength;
  });
  
  // Also log the component type being rendered
  const componentType = content.includes('<thinking>') && content.includes('```python') 
    ? 'Mixed (Thinking + Code)' 
    : content.includes('<thinking>') 
    ? 'Thinking Block' 
    : content.includes('```python') 
    ? 'Code Block' 
    : 'Plain Text';
    
  useEffect(() => {
    console.log(`[Message ${messageIndex}] Component type: ${componentType}`);
  }, [componentType, messageIndex]);
  
  return (
    <div data-message-index={messageIndex} data-content-length={content.length}>
      <StableStreamingMessage content={content} isStreaming={isStreaming} />
    </div>
  );
};

export default DebugStreamingMessage;